import React, {useEffect, useState} from 'react'
import {useAuth} from '../../modules/auth'
import axios from 'axios'
import styles from './BillingView.module.css'
import {FaFilePdf} from 'react-icons/fa'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import moment from 'moment'

interface BillingProps {
  claimDetails: any
}

const Billing: React.FC<BillingProps> = ({claimDetails}) => {
  const user = useAuth()
  const {auth} = useAuth()
  const claimData = claimDetails?.claimDetails?.claimData
    ? JSON.parse(claimDetails.claimDetails.claimData)
    : null
  const detailBenefit = claimDetails?.claimDetails?.detailBenefit
    ? JSON.parse(claimDetails.claimDetails.detailBenefit)
    : null
  const [icdNames, setIcdNames] = useState<Record<string, string>>({})
  const [memberDetail, setMemberDetail] = useState<any>(null)
  const [claim, setClaim] = useState<any>(null)
  const temp = JSON.parse(claimDetails.claimDetails.memberDetail)
  const [fglDocumentUrl, setFglDocumentUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  useEffect(() => {
    if (temp) {
      setMemberDetail(temp)
      setClaim(claimDetails.claimDetails.claimNumber)
    } else {
      console.warn('Claim details not found')
    }
  }, [claimDetails])

  // Fetch FGL Document URL on component mount or when claimDetails change
  useEffect(() => {
    const fetchFglUrl = async () => {
      if (!claimDetails?.claimDetails?.claimNumber) {
        console.warn('Claim number not available for fetching FGL.')
        return
      }
      setIsLoading(true) // Start loading
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Attachment/DownloadFGL/${claimDetails.claimDetails.claimNumber}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch FGL document URL')
        }

        const data = await response.json()

        if (data && data.url) {
          setFglDocumentUrl(data.url) // Store the URL in state
        } else {
          throw new Error('Invalid response format for FGL URL')
        }
      } catch (error) {
        console.error('Error fetching FGL URL:', error)
        setFglDocumentUrl(null) // Reset URL on error
      } finally {
        setIsLoading(false) // Stop loading
      }
    }

    fetchFglUrl() // Fetch the URL when the component mounts or claimDetails change
  }, [claimDetails, auth?.token, user.currentUser?.token])

  // Fetch ICD names
  useEffect(() => {
    const fetchIcdNames = async () => {
      // Check if we already have these ICD names to prevent unnecessary fetches
      const icdCodes = [
        claimData?.IcdCode1,
        claimData?.IcdCode2,
        claimData?.IcdCode3,
        claimData?.IcdCode4,
      ].filter(Boolean)

      // Skip if no ICD codes or if we already have all the names
      const missingIcds = icdCodes.filter((code) => !icdNames[code])
      if (missingIcds.length === 0) return

      const names: Record<string, string> = {...icdNames} // Preserve existing names
      for (const code of missingIcds) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_CLAIM_URL}/api/MstIcd10/GetPaginatedMstIcd10s?searchTerm=${code}&pageNumber=1&pageSize=10`,
            {
              headers: {
                Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
              },
            }
          )
          const matchingIcd = response.data.data.find((icd: any) => icd.code === code)
          if (matchingIcd) {
            names[code] = matchingIcd.name
          }
        } catch (error) {
          console.error(`Error fetching ICD name for ${code}:`, error)
        }
      }
      setIcdNames(names)
    }

    if (claimData) {
      fetchIcdNames()
    }
  }, [claimData, user.currentUser?.token]) // Remove icdNames from dependency array

  // Calculate totals
  const calculateTotals = () => {
    if (!claimData?.MapClaimPaid) return {totalClaim: 0, totalCover: 0, totalExcess: 0}

    return claimData.MapClaimPaid.reduce(
      (acc: any, item: any) => ({
        totalClaim: acc.totalClaim + (Number(item.ClaimAmountTo) || 0),
        totalCover: acc.totalCover + (Number(item.CoverAmount) || 0),
        totalExcess: acc.totalExcess + (Number(item.ExcessAmount) || 0),
      }),
      {totalClaim: 0, totalCover: 0, totalExcess: 0}
    )
  }

  // Filter unique benefits
  const getUniqueBenefits = () => {
    if (!claimData?.MapClaimPaid) return []

    const uniqueBenefits = new Map()
    claimData.MapClaimPaid.forEach((item: any) => {
      const benefit = claimData.TransBenefit?.find((b: any) => b.Id === item.TransBenefitId)
      if (
        benefit &&
        !uniqueBenefits.has(benefit.SubBenefitName) &&
        Number(item.ClaimAmountTo) > 0 &&
        item.isShow
      ) {
        uniqueBenefits.set(benefit.SubBenefitName, item)
      }
    })
    return Array.from(uniqueBenefits.values())
  }

  const handleSavePDF = async () => {
    const content = document.getElementById('document-content')
    if (!content) return

    try {
      const canvas = await html2canvas(content, {
        scale: 2,
        useCORS: true,
        logging: false,
      })

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      })

      const imgWidth = 210
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight)
      pdf.save(`claim-${claimDetails?.claimDetails?.claimNumber || 'document'}.pdf`)
    } catch (error) {
      console.error('Error generating PDF:', error)
    }
  }

  return (
    <>
      <div
        id='document-content'
        className={styles.container}
        style={{height: '80vh' /* Adjust height as needed */}}
      >
        {isLoading && (
          <div
            className={styles.loading} // Keep existing class if it has other styles
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%', // Ensure it fills the container height
            }}
          >
            Loading FGL Document...
          </div>
        )}
        {!isLoading && fglDocumentUrl && (
          <iframe
            // Use Google Docs viewer for broader compatibility (PDF, DOCX, etc.)
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(
              fglDocumentUrl
            )}&embedded=true`}
            style={{
              width: '100%',
              height: '100%', // Make iframe fill the container
              border: 'none', // Removes browser border
            }}
            title='FGL Document Preview'
            frameBorder='0' // Also removes browser border (legacy attribute)
          />
        )}
        {!isLoading && !fglDocumentUrl && (
          <div
            className={styles.error} // Keep existing class
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            Failed to load FGL document preview.
          </div>
        )}
      </div>
    </>
  )
}

export default Billing
